// Tells a Sentinel about every ticket that reaches them on any page of the app: a ticket routed
// to them, and a customer replying on a ticket they hold. Mounted once in the app layout
// (components/sentinel-assignment-alerts.tsx), because a Sentinel with Kim open on any page is
// online and can be given tickets; the profile menu shows the settings below.
//
// - Kim in front (visible and focused): a toast with an Open button.
// - Kim in another tab, behind another app, or not focused: a Chrome notification as well;
//   clicking it brings this tab forward and opens the ticket. Chrome keeps a background tab's
//   socket connected, so the event arrives at once. A reply on a ticket that already notified
//   alerts again (`renotify`) instead of silently replacing the old one.
// - Either way, while not in front: a count in the tab title until they come back.
// - A ticket they took themselves (Assign to me, or a Send that claimed it) alerts nothing.
// - Every arrival plays a short ding-dong, in front or not, unless Notifications are off, which
//   also stops the Chrome notification. They count as off until Chrome has been asked, so turning
//   them on is what asks it. The choice is remembered in this browser.
//
// No service worker or push service: a Sentinel with no Kim tab open is offline and gets no
// tickets. Two open Kim tabs show one notification, because each is tagged by ticket.

import { atom, useAtom, useAtomValue } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "@/components/ui/sonner";
import { currentUserAtom } from "@/hooks/use-auth";
import { useBusinessUnits } from "@/hooks/use-business-units";
import { isOwnTicketAction } from "@/lib/sentinel-own-actions";
import { latestSentinelAssignmentAtom } from "@/stores/websocket.store";
import type { SentinelTicketAssignedPayload } from "@/types/websocket.types";

type Permission = NotificationPermission | "unsupported";

const SOUND_URL = "/sounds/sentinel-ding-dong.m4a";
const SOUND_MUTED_KEY = "sentinel-sound-muted";

/** Browser storage can be missing or refuse access (private windows); sound is then on. */
function readSoundMuted(): boolean {
  try {
    return localStorage.getItem(SOUND_MUTED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeSoundMuted(muted: boolean): void {
  try {
    if (muted) localStorage.setItem(SOUND_MUTED_KEY, "1");
    else localStorage.removeItem(SOUND_MUTED_KEY);
  } catch {
    // Not remembered in this browser; the choice still holds until the page reloads.
  }
}

/** The Notifications choice, shared by the app-wide alerts and the profile menu. Null until set here. */
const soundMutedAtom = atom<boolean | null>(null);

let ding: HTMLAudioElement | null = null;

/**
 * Fetch and decode the chime as soon as the workspace opens. Created on the first arrival
 * instead, the first ding waited for the download and decode and played late.
 */
function loadDing(): HTMLAudioElement | null {
  if (typeof Audio === "undefined") return null;
  if (!ding) {
    ding = new Audio(SOUND_URL);
    ding.preload = "auto";
    ding.load();
  }
  return ding;
}

function playDing(): void {
  const audio = loadDing();
  if (!audio) return;
  audio.currentTime = 0;
  // Refused until the page has had a click; the toast and notification still show.
  void audio.play().catch(() => undefined);
}

function currentPermission(): Permission {
  return typeof window !== "undefined" && "Notification" in window
    ? Notification.permission
    : "unsupported";
}

/** Visible and focused: the Sentinel is looking at Kim right now. */
function workspaceInFront(): boolean {
  return document.visibilityState === "visible" && document.hasFocus();
}

/** One key per arrival: a second reply on the same ticket is new; the same event re-delivered is not. */
function arrivalKey(arrival: SentinelTicketAssignedPayload): string {
  return `${arrival.ticketId}:${arrival.reason ?? "assigned"}:${arrival.at ?? ""}`;
}

function arrivalText(
  arrival: SentinelTicketAssignedPayload,
  brand: string | null,
): { title: string; body: string } {
  if (arrival.reason === "customer_replied") {
    return {
      title: brand ? `New reply from ${brand}` : "Your customer replied",
      body: "A customer replied on your ticket. Click to open it.",
    };
  }
  return {
    title: brand ? `New ticket from ${brand}` : "New ticket assigned to you",
    body: "Click to open it in the Sentinel workspace.",
  };
}

export interface AssignmentNotifications {
  permission: Permission;
  /** Must be called from a click: Chrome only lets a page ask from a user gesture. */
  requestPermission: () => Promise<void>;
  soundMuted: boolean;
  setSoundMuted: (muted: boolean) => void;
}

/** The notification permission and the Notifications choice, for the profile menu. Shows no alert. */
export function useSentinelAlertSettings(): AssignmentNotifications {
  const [permission, setPermission] = useState<Permission>(currentPermission);
  const [storedMuted, setStoredMuted] = useAtom(soundMutedAtom);
  const soundMuted = storedMuted ?? readSoundMuted();

  const requestPermission = useCallback(async () => {
    if (currentPermission() === "unsupported") return;
    setPermission(await Notification.requestPermission());
  }, []);

  const setSoundMuted = useCallback(
    (muted: boolean) => {
      writeSoundMuted(muted);
      setStoredMuted(muted);
    },
    [setStoredMuted],
  );

  return { permission, requestPermission, soundMuted, setSoundMuted };
}

/** Alerts each arrival addressed to the signed-in user. Mount once, app-wide. */
export function useSentinelAssignmentNotifications(
  onOpen: (ticketId: number) => void,
): AssignmentNotifications {
  const assignment = useAtomValue(latestSentinelAssignmentAtom);
  const me = useAtomValue(currentUserAtom);
  const { businessUnits } = useBusinessUnits();
  const settings = useSentinelAlertSettings();
  const { soundMuted } = settings;
  useEffect(() => {
    loadDing();
  }, []);
  const soundMutedRef = useRef(soundMuted);
  soundMutedRef.current = soundMuted;
  const [unseen, setUnseen] = useState(0);
  const notified = useRef(new Set<string>());
  const baseTitle = useRef(
    typeof document === "undefined" ? "" : document.title,
  );
  const openRef = useRef(onOpen);
  openRef.current = onOpen;

  useEffect(() => {
    if (!assignment || String(assignment.userId) !== String(me?.id)) return;
    const key = arrivalKey(assignment);
    if (notified.current.has(key)) return;
    notified.current.add(key);
    // A ticket the Sentinel just took themselves: they already know. A reply still alerts.
    if (
      assignment.reason !== "customer_replied" &&
      isOwnTicketAction(assignment.ticketId)
    )
      return;

    const brand =
      assignment.buId == null
        ? null
        : (businessUnits.find((unit) => unit.id === String(assignment.buId))
            ?.name ?? null);
    const { title, body } = arrivalText(assignment, brand);
    const open = () => openRef.current(assignment.ticketId);
    // Off until Chrome has been asked: the switch that asks it is the same one that turns them on.
    if (!soundMutedRef.current && currentPermission() !== "default") playDing();

    if (document.visibilityState === "visible")
      toast(title, { action: { label: "Open", onClick: open } });
    if (workspaceInFront()) return;

    setUnseen((count) => count + 1);
    // Notifications off in the profile menu silences the desktop alert as well as the sound.
    if (soundMutedRef.current || currentPermission() !== "granted") return;
    // `renotify` is supported by Chrome but missing from TypeScript's DOM types.
    const options: NotificationOptions & { renotify: boolean } = {
      body,
      tag: `sentinel-ticket-${assignment.ticketId}`,
      renotify: true,
      icon: "/android-chrome-192x192.png",
    };
    const notification = new Notification(title, options);
    notification.onclick = () => {
      window.focus();
      open();
      notification.close();
    };
  }, [assignment, me?.id, businessUnits]);

  // "(2) Sentinel workspace" while arrivals wait unseen; cleared when they come back to it.
  useEffect(() => {
    if (unseen === 0) return;
    document.title = `(${unseen}) ${baseTitle.current}`;
  }, [unseen]);

  useEffect(() => {
    const title = baseTitle.current;
    const onBack = () => {
      if (!workspaceInFront()) return;
      setUnseen(0);
      document.title = title;
    };
    document.addEventListener("visibilitychange", onBack);
    window.addEventListener("focus", onBack);
    return () => {
      document.removeEventListener("visibilitychange", onBack);
      window.removeEventListener("focus", onBack);
      document.title = title;
    };
  }, []);

  return settings;
}
